import Editor, { type OnMount } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { useRef, useState } from "react";
import { CodeBracketIcon } from "@heroicons/react/24/outline";
import ButtonBase from "../Buttons/ButtonBase";
import {
  CheckIcon,
  ClipboardDocumentIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";
import ToastComp from "../Toasts/index";
import toast from "react-hot-toast";

interface CodeEditorProps {
  title?: string;
  value: string;
  onChange?: (value: string) => void;
  language?: string;
  height?: string;
  theme?: "vs" | "vs-dark";
  readOnly?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  className?: string;
  error?: { message?: string };
}

const CodeEditor = ({
  title = "Code Editor",
  value,
  onChange,
  language = "json",
  height = "300px",
  theme = "vs-dark",
  readOnly = false,
  onValidityChange,
  className = "",
  error,
}: CodeEditorProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const handleEditorMount: OnMount = (editorInstance) => {
    editorRef.current = editorInstance;
  };

  const handleFormat = async () => {
    await editorRef.current?.getAction("editor.action.formatDocument")?.run();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      toast((toast) => (
        <ToastComp
          toastContent={toast}
          message={
            error instanceof Error ? error.message : "Something went wrong"
          }
          icon={<ExclamationCircleIcon className="size-5 text-error" />}
        />
      ));
    }
  };

  const handleValidate = (markers: editor.IMarker[]) => {
    // severity 8 represents an Error in Monaco Editor.
    const hasErrors = markers.some((marker) => marker.severity === 8);
    onValidityChange?.(!hasErrors);
  };

  return (
    <div
      className={`flex flex-col gap-2 border p-4 rounded-md w-full ${
        error ? "border-red-500" : "border-lightGray"
      } ${className}`}
    >
      {/* Header */}
      <section className="flex items-center justify-between">
        <h3 className="text-base font-semibold">{title}</h3>
        <div className="flex justify-end gap-2">
          {/* Option */}
          <ButtonBase
            buttonType="secondary"
            icon={
              isCopied ? (
                <CheckIcon className="size-5" />
              ) : (
                <ClipboardDocumentIcon className="size-5" />
              )
            }
            className="self-end px-4 h-7"
            onClick={handleCopy}
            title={isCopied ? "Copied!" : "Copy Code"}
          />
          <ButtonBase
            buttonType="secondary"
            icon={<CodeBracketIcon className="size-5" />}
            className="self-end px-4 h-7"
            onClick={handleFormat}
            title="Format Code"
          />
        </div>
      </section>

      <div className="border-t border-lightGray" />

      {/* Code Editor */}
      <div className="w-full [&_.monaco-editor]:rounded-md [&_.overflow-guard]:rounded-md">
        <Editor
          height={height}
          language={language}
          theme={theme}
          value={value}
          onChange={(newValue) => onChange?.(newValue ?? "")}
          onMount={handleEditorMount}
          onValidate={handleValidate}
          options={{
            minimap: { enabled: false },
            formatOnPaste: true,
            formatOnType: true,
            automaticLayout: true,
            scrollBeyondLastLine: false,
            tabSize: 2,
            readOnly,
            wordWrap: "on",
            folding: true,
            lineNumbers: "on",
            scrollBeyondLastColumn: 2,
            padding: { top: 8 },
          }}
        />
      </div>

      {error?.message && (
        <p
          className="text-xs text-error font-medium pl-2 mt-0.5 break-words text-pretty"
          role="alert"
        >
          {error.message}
        </p>
      )}
    </div>
  );
};

export default CodeEditor;
